import React, { useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Avatar,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import { Visibility, VisibilityOff, School } from '@mui/icons-material'
import { useAuth } from '../../contexts/AuthContext'
import axios from 'axios'

const LoginForm = () => {
  const { login, loading } = useAuth()
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [openForgotPassword, setOpenForgotPassword] = useState(false)
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotSuccess, setForgotSuccess] = useState('')
  const [forgotError, setForgotError] = useState('')
  const [forgotLoading, setForgotLoading] = useState(false)

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    if (error) setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (!formData.email || !formData.password) {
      setError('Por favor complete todos los campos')
      return
    }

    const result = await login(formData)
    
    if (!result.success) {
      setError(result.error)
    }
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const handleForgotPassword = async () => {
    setForgotError('')
    setForgotSuccess('')

    if (!forgotEmail) {
      setForgotError('Por favor ingresa tu correo electrónico')
      return
    }

    setForgotLoading(true)
    try {
      const response = await axios.post('/api/auth/forgot-password', {
        email: forgotEmail
      })
      setForgotSuccess(response.data.message)
      setForgotEmail('')
    } catch (error) {
      setForgotError(error.response?.data?.detail || 'Error al enviar el correo de recuperación')
    } finally {
      setForgotLoading(false)
    }
  }

  const handleCloseForgotDialog = () => {
    setOpenForgotPassword(false)
    setForgotEmail('')
    setForgotError('')
    setForgotSuccess('')
  }


  return (
    <Card
      sx={{
        maxWidth: 400,
        width: '100%',
        mx: 2,
        boxShadow: 6,
        borderRadius: 3,
        background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 3
          }}
        >
          <Avatar
            sx={{
              m: 1,
              bgcolor: 'primary.main',
              width: 56,
              height: 56
            }}
          >
            <School fontSize="large" />
          </Avatar>
          
          <Typography variant="h5" component="h1" gutterBottom fontWeight="bold">
            Sistema de Gestión de Calidad
          </Typography>

          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 1 }}>
            Escuela Normal Superior del Alto Sinú
          </Typography>

          <Typography variant="caption" color="primary.main" align="center" sx={{ fontWeight: 'medium' }}>
            Acceso al SGC ENSAS
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Correo Electrónico"
            name="email"
            autoComplete="email"
            autoFocus
            value={formData.email}
            onChange={handleChange}
            error={!formData.email && !!error}
            disabled={loading}
          />
          
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Contraseña"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            value={formData.password}
            onChange={handleChange}
            error={!formData.password && !!error}
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={togglePasswordVisibility}
                    edge="end"
                    disabled={loading}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            sx={{
              mt: 3,
              mb: 2,
              py: 1.5,
              borderRadius: 2,
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4
              }
            }}
            disabled={loading}
          >
            {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </Button>

          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => setOpenForgotPassword(true)}
              sx={{ cursor: 'pointer' }}
              disabled={loading}
            >
              ¿Olvidaste tu contraseña?
            </Link>
          </Box>
        </Box>

        {/* Diálogo para recuperación de contraseña */}
        <Dialog open={openForgotPassword} onClose={handleCloseForgotDialog} maxWidth="xs" fullWidth>
          <DialogTitle>Recuperar Contraseña</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              {forgotSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {forgotSuccess}
                </Alert>
              )}
              {forgotError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {forgotError}
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Ingresa tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña.
              </Typography>
              <TextField
                fullWidth
                label="Correo Electrónico"
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                disabled={forgotLoading}
                autoFocus
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseForgotDialog} disabled={forgotLoading}>
              Cancelar
            </Button>
            <Button
              onClick={handleForgotPassword}
              variant="contained"
              disabled={forgotLoading}
            >
              {forgotLoading ? 'Enviando...' : 'Enviar'}
            </Button>
          </DialogActions>
        </Dialog>

        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            textAlign: 'center'
          }}
        >
          <Typography variant="caption" color="text.secondary">
            SGC v1.0.0 - ENSAS • 2025
          </Typography>
        </Box>
      </CardContent>
    </Card>
  )
}

export default LoginForm